import type { NotificationCategory, NotificationVariables } from '@tempo-io/notification-dto';
import { creditPoolLowDescriptor } from './credits';
import {
  demandFrameworkChangedDescriptor,
  demandReviewerAssignedDescriptor,
  demandStageChangedDescriptor,
} from './demands';
import {
  commentMentionDescriptor,
  feedbackDescriptor,
  ideaCommentsDescriptor,
  staleIdeasDescriptor,
} from './ideas-and-feedback';
import { newCriticalInsightDescriptor, newHighInsightDescriptor } from './insights';
import { invitesDescriptor, workIntegrationReauthDescriptor } from './invites-and-reauth';
import {
  aiDashboardGenerationCompletedDescriptor,
  exportCompletionDescriptor,
  objectiveDriftDetectedDescriptor,
  objectiveHealthChangedDescriptor,
  planPublishedDescriptor,
  portfolioCreatedDescriptor,
  scenarioOperationFailedDescriptor,
  teamAssignmentChangedDescriptor,
} from './loop';
import {
  recommendationAssignedDescriptor,
  recommendationCompletedDescriptor,
  recommendationDismissedDescriptor,
  recommendationReopenedDescriptor,
  recommendationRestoredDescriptor,
  recommendationUnassignedDescriptor,
} from './recommendation-moves';
import {
  capacityOrphanedInstanceDescriptor,
  trialExtendedDescriptor,
  trialsDescriptor,
} from './trials-and-capacity';
import type { CategoryDescriptor } from './types';
import {
  workIntegrationDisconnectedDescriptor,
  workIntegrationExcludedTeamsDescriptor,
  workIntegrationPublishCompleteDescriptor,
  workIntegrationScheduleDisabledDescriptor,
  workIntegrationSetupCompleteDescriptor,
  workIntegrationSyncErrorDescriptor,
} from './work-integration';

export const NOTIFICATION_REGISTRY: { [C in NotificationCategory]: CategoryDescriptor<C> } = {
  invites: invitesDescriptor,
  trials: trialsDescriptor,
  trial_extended: trialExtendedDescriptor,
  work_integration_reauth: workIntegrationReauthDescriptor,
  work_integration_schedule_disabled: workIntegrationScheduleDisabledDescriptor,
  work_integration_sync_error: workIntegrationSyncErrorDescriptor,
  work_integration_setup_complete: workIntegrationSetupCompleteDescriptor,
  work_integration_disconnected: workIntegrationDisconnectedDescriptor,
  work_integration_excluded_teams: workIntegrationExcludedTeamsDescriptor,
  work_integration_publish_complete: workIntegrationPublishCompleteDescriptor,
  capacity_orphaned_instance: capacityOrphanedInstanceDescriptor,
  portfolio_created: portfolioCreatedDescriptor,
  team_assignment_changed: teamAssignmentChangedDescriptor,
  plan_published: planPublishedDescriptor,
  scenario_operation_failed: scenarioOperationFailedDescriptor,
  demand_framework_changed: demandFrameworkChangedDescriptor,
  demand_reviewer_assigned: demandReviewerAssignedDescriptor,
  demand_stage_changed: demandStageChangedDescriptor,
  objective_health_changed: objectiveHealthChangedDescriptor,
  objective_drift_detected: objectiveDriftDetectedDescriptor,
  feedback: feedbackDescriptor,
  idea_comments: ideaCommentsDescriptor,
  comment_mention: commentMentionDescriptor,
  stale_ideas: staleIdeasDescriptor,
  export_completion: exportCompletionDescriptor,
  ai_dashboard_generation_completed: aiDashboardGenerationCompletedDescriptor,
  new_critical_insight: newCriticalInsightDescriptor,
  new_high_insight: newHighInsightDescriptor,
  credit_pool_low: creditPoolLowDescriptor,
  recommendation_assigned: recommendationAssignedDescriptor,
  recommendation_completed: recommendationCompletedDescriptor,
  recommendation_dismissed: recommendationDismissedDescriptor,
  recommendation_reopened: recommendationReopenedDescriptor,
  recommendation_restored: recommendationRestoredDescriptor,
  recommendation_unassigned: recommendationUnassignedDescriptor,
};

export type { CategoryDescriptor, DataFor } from './types';
export { extractRelativeActionUrl, rewriteLegacyIdeaManagementPath } from './types';

export const getCategoryDescriptor = <C extends NotificationCategory>(
  category: C,
): CategoryDescriptor<C> => NOTIFICATION_REGISTRY[category];

export const isMandatoryInAppCategory = (category: NotificationCategory | null): boolean =>
  category !== null && getCategoryDescriptor(category).nonDisableable === true;

export const getNotificationActionUrl = (
  variables: NotificationVariables,
  organizationId: string,
): string => {
  const entry = getCategoryDescriptor(variables.category);
  return entry.actionUrl(variables.data, organizationId);
};
